import { pubText, pubPath } from "../i18n/publicTranslation";
import React, { useEffect, useState } from 'react';
import { Button, Box, CircularProgress, ListItemButton, ListItemText } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useAppSelector } from '../store/hooks';
import { useNavigate } from 'react-router-dom';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { formatTrialUnlockPrice, getTrialOfferRemainingMs } from '../util/trialOffer';

interface LoginButtonProps {
  inDrawer?: boolean;
  onDrawerClose?: () => void;
}

const LoginButton: React.FC<LoginButtonProps> = ({ inDrawer = false, onDrawerClose }) => {
  const { isLoading, isAuthenticated } = useAuth();
  const user = useAppSelector((state) => ({
    email: state.app.email,
    userId: state.app.userId,
    trialOffer: state.app.trialOffer,
  }));
  const [trialOfferRemainingMs, setTrialOfferRemainingMs] = useState(0);
  const navigate = useNavigate();

  useFeatureGates(user.userId || '', Boolean(user.userId));

  useEffect(() => {
    const updateRemaining = () => {
      setTrialOfferRemainingMs(getTrialOfferRemainingMs(user.trialOffer));
    };

    updateRemaining();
    const interval = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(interval);
  }, [user.trialOffer]);

  const isLoggedIn = Boolean(isAuthenticated && user.userId);
  const hasActiveTrialOffer = Boolean(
    isLoggedIn &&
    user.trialOffer?.enabled === true &&
    trialOfferRemainingMs > 0,
  );
  const trialUnlockPrice = formatTrialUnlockPrice(
    user.trialOffer?.unlockAmountCents,
    user.trialOffer?.unlockCurrency,
  );
  const premiumCtaLabel = !isLoggedIn
    ? 'Start Trial'
    : hasActiveTrialOffer
      ? `Start ${trialUnlockPrice} Trial`
      : 'View Premium';
  const premiumCtaPath = hasActiveTrialOffer || !isLoggedIn
    ? '/payment?category=subscription&trial=true'
    : '/payment?category=subscription';

  const handleLogin = () => {
    if (onDrawerClose) {
      onDrawerClose();
    }
    navigate(pubPath('/signin'));
  };

  const handleStartTrial = () => {
    if (onDrawerClose) {
      onDrawerClose();
    }
    // Navigate to payment page with subscription tab - user will be redirected to sign in if needed
    navigate(pubPath(premiumCtaPath));
  };

  if (isLoading) {
    if (inDrawer) {
      return (
        <ListItemButton
          disabled
          sx={{
            borderRadius: '12px',
            background: 'rgba(216, 163, 99, 0.1)',
            border: '1px solid rgba(216, 163, 99, 0.3)',
            transition: 'all 0.3s ease',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <CircularProgress size={16} sx={{ color: '#D8A363' }} />
            <ListItemText
              primary={pubText("Loading...")}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  color: '#D8A363',
                  fontSize: '1rem',
                  transition: 'color 0.3s ease'
                }
              }}
            />
          </Box>
        </ListItemButton>
      );
    }
    return (
      <Button
        variant="outlined"
        disabled
        sx={{
          color: '#fff',
          border: '1px solid rgba(216, 163, 99, 0.9)',
          background: 'rgba(216, 163, 99, 0.1)',
          backdropFilter: 'blur(8px)',
          borderRadius: '24px',
          padding: '6px 16px',
          minWidth: 'auto',
        }}
      >
        <CircularProgress size={28} sx={{ color: '#D8A363' }} />
      </Button>
    );
  }

  if (isAuthenticated && user) {
    // For authenticated users, don't show any button in header (sign out is in settings)
    return null;
  }

  if (inDrawer) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
        <ListItemButton
          onClick={handleStartTrial}
          sx={{
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)',
            py: 1.5,
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(216, 163, 99, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #E8A54C 0%, #FF9F32 50%, #FFB347 100%)',
              boxShadow: '0 4px 12px rgba(216, 163, 99, 0.4)',
            }
          }}
        >
          <ListItemText
            primary={pubText(premiumCtaLabel)}
            sx={{
              textAlign: 'center',
              '& .MuiTypography-root': {
                fontWeight: 600,
                color: '#1a1625',
                fontSize: '1rem',
              }
            }}
          />
        </ListItemButton>
        <ListItemButton
          onClick={handleLogin}
          sx={{
            borderRadius: '12px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              background: 'rgba(255,255,255,0.1)',
              borderColor: 'rgba(255,255,255,0.3)'
            }
          }}
        >
          <ListItemText
            primary={pubText("Sign In")}
            sx={{
              textAlign: 'center',
              '& .MuiTypography-root': {
                fontWeight: 500,
                color: '#fff',
                fontSize: '1rem',
              }
            }}
          />
        </ListItemButton>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
      <Button
        variant="outlined"
        onClick={handleLogin}
        sx={{
          color: '#fff',
          fontWeight: 500,
          fontSize: '0.9rem',
          textTransform: 'none',
          minHeight: 44,
          padding: '8px 18px',
          borderRadius: '22px',
          border: '1px solid rgba(255,255,255,0.3)',
          background: 'transparent',
          transition: 'all 0.3s ease',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderColor: 'rgba(255,255,255,0.5)',
          },
        }}
      >{pubText(" Sign In ")}</Button>
      <Button
        onClick={handleStartTrial}
        sx={{
          background: 'linear-gradient(135deg, #D8A363 0%, #E8A54C 50%, #FF9F32 100%)',
          color: '#1a1625',
          fontWeight: 600,
          fontSize: '0.9rem',
          textTransform: 'none',
          minHeight: 44,
          padding: '8px 20px',
          borderRadius: '22px',
          transition: 'all 0.3s ease',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(216, 163, 99, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #E8A54C 0%, #FF9F32 50%, #FFB347 100%)',
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(216, 163, 99, 0.4)',
          },
        }}
      >
        {pubText(premiumCtaLabel)}
      </Button>
    </Box>
  );
};

export default LoginButton;
